import React from 'react';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';

import { DefaultFilledButton, SecondaryButton } from '@magoosh/lib/button';
import SimpleDonutChart from '@magoosh/lib/charts/donut_chart/simple';
import { ExamSectionIcon } from '@magoosh/lib/icons';
import { AudioSetupButton } from '@magoosh/organisms/audio/setup';
import StartButton from '@magoosh/organisms/practice_sessions/start_button';
import { paths } from 'config/path_helpers';
import { pluralize } from 'utilities/pluralize';
import { secondsFormattedAsMinutes } from 'utilities/time';

import ContextualLink from '@test_prep/app/components/contextual_link';
import { useAppContext } from '@test_prep/app/context';

import { ExamSection } from '../../types';

import colors from '@magoosh/lib/styles/colors.module.scss';

export default (props: ExamSection) => {
  const { branding, features } = useAppContext().data;

  const startQuickPractice = (group) => {
    const path = paths.startQuickPractice({
      'practice_session[exam_section_id]': props.id,
      'practice_session[tag_ids]': group.subjectTagIds.join(','),
    })
    window.location.href = path;
  };

  return (
    <div className="d-flex flex-column gap-m">
      <div className="d-flex flex-column flex-lg-row gap-s">
        <div className="align-items-center d-flex flex-1 gap-s">
          <ExamSectionIcon subject={props.subject} name={props.name} />
          <h3 className="u-margin-A-n">{props.name}</h3>
        </div>

        {
          props.scoreEstimate && (
            <div>
              <div className="d-flex gap-xs justify-content-lg-end small">
                <OverlayTrigger
                  placement="top"
                  overlay={
                    <Tooltip id={`tooltip-estimated-score-${props.id}`}>
                      We provide a score estimate after you answer {pluralize(props.scoreEstimate.answerCountThreshold, `${props.name} question`)}, based on actual {branding.examName} scores from previous Magoosh students. The more questions you answer, the more accurate your estimate will be. (Only your most recent answer for each question is used.)
                    </Tooltip>
                  }
                >
                  <div className="text-underdash">Estimated Score:</div>
                </OverlayTrigger>

                <div>
                  {
                    props.scoreEstimate.low && props.scoreEstimate.high ? (
                      props.scoreEstimate.low === props.scoreEstimate.high ? (
                        props.scoreEstimate.low
                      ) : (
                        `${props.scoreEstimate.low} - ${props.scoreEstimate.high}`
                      )
                    ) : 'Unknown'
                  }
                </div>
              </div>
            </div>
          )
        }
      </div>

      {
        props.groups.map((group, idx) => (
          <div key={idx} className="d-flex flex-column gap-m">
            {
              (group.name !== props.name || group.performance.type === 'value') && (
                <div className="d-flex flex-column gap-xxs">
                  {group.name !== props.name && <h4 className="u-margin-A-n">{group.name}</h4>}
                  {
                    group.performance.type === 'value' && (
                      <div className="small">
                        Scores range from {group.performance.range.low} to {group.performance.range.high}
                      </div>
                    )
                  }
                </div>
              )
            }

            <div className="align-items-lg-center d-flex flex-column flex-lg-row gap-m gap-lg-l">
              <div className="align-items-center d-flex flex-1 gap-m">
                {/*TODO - match color palette of analytics*/}
                <div className="flex-none">
                  <SimpleDonutChart
                    colorPalette={{ dark: colors.dataDarkPurple, light: colors.dataMutedPurple }}
                    data={{
                      percentage: group.performance.type === 'percentage',
                      range: group.performance.range,
                      value: group.performance.value
                    }}
                    size={100}
                  />
                </div>

                <table className="d-flex flex-none">
                  <tbody>
                    <tr>
                      <td style={{ padding: 0, paddingRight: 10 }}>
                        <span className="hidden-xs">Questions Answered:</span>
                        <span className="visible-xs">Answered:</span>
                      </td>
                      <td style={{ padding: 0 }}>{group.questions.answered}</td>
                    </tr>

                    <tr>
                      <td style={{ padding: 0, paddingRight: 10 }}>Your Pace:</td>
                      <td style={{ padding: 0 }}>{secondsFormattedAsMinutes(group.questions.paceInSeconds.user, false)}</td>
                    </tr>

                    <tr>
                      <td style={{ padding: 0, paddingRight: 10 }}>Others&apos; Pace:</td>
                      <td style={{ padding: 0 }}>{secondsFormattedAsMinutes(group.questions.paceInSeconds.others, false)}</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <div className="visible-lg" style={{ background: '#EEEEEF', height: 100, width: 1 }} />

              <div className="d-flex flex-column gap-xs">
                <div className="d-flex flex-1 gap-s">
                  <StartButton
                    block
                    className="flex-1"
                    includesSpeaking={props.subject === 'speaking'}
                    onStart={() => startQuickPractice(group)}
                    size="sm"
                  >
                    Quick Practice
                  </StartButton>

                  <ContextualLink
                    className="flex-1 text-hover-no-decoration text-no-decoration"
                    href={paths.startPractice({
                      'practice_session[exam_section_id]': props.id,
                      'practice_session[tag_ids]': group.subjectTagIds.join(',')
                    })}
                  >
                    <SecondaryButton block size="sm">
                      Custom Practice
                    </SecondaryButton>
                  </ContextualLink>
                </div>

                <div className="visible-lg">
                  <div className="d-flex justify-content-center text-gray-light" style={{ fontSize: 12 }}>
                    {pluralize(group.questions.remaining, 'practice question')} left
                  </div>
                </div>
              </div>
            </div>

            {idx !== props.groups.length - 1 && <hr className="u-margin-A-n" />}
          </div>
        ))
      }

      <hr className="u-margin-A-n" />

      <div className="align-items-center d-flex gap-s flex-wrap">
        <ContextualLink href={paths.lessons()}>
          <DefaultFilledButton size="sm">
            <span className="visible-xs">Lessons</span>
            <span className="hidden-xs">View Lessons</span>
          </DefaultFilledButton>
        </ContextualLink>

        {
          features.analytics && (
            <ContextualLink href={paths.analytics({ sections: props.slug })}>
              <DefaultFilledButton size="sm">
                <span className="visible-xs">Analytics</span>
                <span className="hidden-xs">View Analytics</span>
              </DefaultFilledButton>
            </ContextualLink>
          )
        }

        <ContextualLink href={paths.examSectionNotes(props.slug)}>
          <DefaultFilledButton size="sm">
            My Notes ({props.notes})
          </DefaultFilledButton>
        </ContextualLink>

        {
          props.subject === 'speaking' && (
            <div className="d-flex flex-1 justify-content-lg-end">
              <AudioSetupButton className="small text-gray-light" />
            </div>
          )
        }
      </div>
    </div>
  );
};
